import React, { useEffect } from 'react';

import DefaultApiLimit from '../../../config/defaultLimit';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  disableGlobalModelFilterApplied,
  fetchAllGlobalModels,
  fetchGlobalModelFilters,
  setGlobalModelLimit,
  setGlobalModelPage,
  setGlobalModelSortBy,
  setGlobalModelSortField,
  updateGlobalModelFilters,
} from '../../../redux/slices/GlobalModelSlice';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';

/**
 * Global models list component — displays globally approved models.
 * @returns {React.JSX.Element} Global models table
 */
function Global() {
  const dispatch = useAppDispatch();
  const {
    models,
    loading,
    totalResults,
    totalPages,
    page,
    limit,
    filters,
    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.globalModel);

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchAllGlobalModels({
        page: DefaultApiLimit.page,
        limit,
        sortBy,
        sortField,
        ...queryParameters,
      }),
    );
  }, []);

  useEffect(() => {
    if (isFilterUpdated) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      dispatch(
        fetchAllGlobalModels({
          page: DefaultApiLimit.page,
          limit,
          sortBy,
          sortField,
          ...queryParameters,
        }),
      );
      dispatch(disableGlobalModelFilterApplied());
    }
  }, [isFilterUpdated]);

  /**
   * Handles sort change from CustomTable
   * @param {any} sortData - Sort data payload
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setGlobalModelSortBy(sortData.sortBy));
    dispatch(setGlobalModelSortField(sortData.sortField));
    dispatch(
      fetchAllGlobalModels({
        ...queryParameters,
        sortBy: sortData.sortBy,
        sortField: sortData.sortField,
        page: DefaultApiLimit.page,
        limit,
      }),
    );
  };

  /**
   * Handles pagination and limit changes
   * @param {any} value - Page and limit data
   * @param {any} value.page - The page prop
   * @param {any} value.limit - The limit prop
   */
  const handlePageLimitChange = (value: { page?: number; limit?: number }) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    if (value.page !== undefined) dispatch(setGlobalModelPage(value.page));
    if (value.limit !== undefined) dispatch(setGlobalModelLimit(value.limit));
    dispatch(
      fetchAllGlobalModels({
        ...queryParameters,
        ...value,
        sortField,
        sortBy,
      }),
    );
  };

  /**
   * Refreshes global model data using current filters
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchAllGlobalModels({
        ...queryParameters,
        page,
        limit,
        sortField,
        sortBy,
      }),
    );
  };

  const column = [
    {
      label: 'Model Name',
      accessorKey: 'name',
    },
    {
      label: 'Brand Name',
      accessorKey: 'brandId',
      /**
       * Renders brand name
       * @param {any} currentRow - row data
       * @returns {any} JSX element
       */
      customData: (currentRow: any) => (
        <span>
          {currentRow.brand?.name ||
            currentRow.brandId?.name ||
            currentRow.brandName ||
            currentRow.brandId ||
            '-'}
        </span>
      ),
    },
    {
      label: 'Media',
      accessorKey: 'imageUrls',
      /**
       * Renders model media preview
       * @param {any} currentRow - row data
       * @returns {any} JSX element
       */
      customData: (currentRow: any) => {
        const image =
          currentRow.imageUrl ||
          currentRow.imageUrls?.[0]?.signedUrl ||
          currentRow.imageUrls?.[0]?.url;
        return image ? (
          <div className="model-media-cell">
            <div className="model-media-wrapper">
              <img src={image} alt={currentRow.name || 'Model'} className="model-media-thumbnail" />
            </div>
          </div>
        ) : (
          <span>-</span>
        );
      },
    },
  ];

  return (
    <CustomTable
      dataKey="GlobalModelManagement"
      header={column}
      data={models}
      serialNo
      version="v2"
      loading={loading}
      page={page}
      limit={limit}
      height={350}
      created
      lastUpdated
      totalPages={totalPages}
      totalResults={totalResults}
      sortData={{ sortBy, sortField }}
      handleSortChange={handleSortFunction}
      pageLimitChange={handlePageLimitChange}
      sizeChangeButtonRequired
      filters={filters}
      filtersToDispatch={updateGlobalModelFilters}
      filterListLoading={isFilterUpdated}
      filterListToDispatch={fetchGlobalModelFilters}
      refreshRequired
      handleRefresh={handleRefresh}
      hoverLabel="Refresh"
      refershCurrentPages={handleRefresh}
      onHiddenColumnsChange={() => {}}
    />
  );
}

export default Global;
